import React, { useEffect, useState } from "react";
import { MessageSquare, ArrowUpRight, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { getRecentChat, getAdminUser } from "@/lib/api";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffSec = Math.round((Date.now() - d.getTime()) / 1000);
    if (diffSec < 60) return "just now";
    if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m`;
    if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return ""; }
};

export default function ChatWidget({ onOpenChat, unread = 0 }) {
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const me = getAdminUser();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = await getRecentChat(5);
        if (!cancelled) setMessages(d.messages || []);
      } catch { /* non-fatal */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    const t = setInterval(load, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E2E8F0" }} data-testid="dashboard-chat-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="grid place-items-center h-9 w-9 rounded-lg"
                style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
            <MessageSquare className="h-4 w-4" />
          </span>
          <div>
            <h3 className="font-display text-base font-semibold leading-tight" style={{ color: "#0B2545" }}>Team Chat</h3>
            <p className="text-xs" style={{ color: "#64748B" }}>
              {unread > 0 ? `${unread} unread message${unread === 1 ? "" : "s"}` : "All caught up"}
            </p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onOpenChat}
          className="h-8 text-xs"
          style={{ color: "#7A5E12" }}
          data-testid="dashboard-chat-open"
        >
          Open chat <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
        </Button>
      </div>

      {loading ? (
        <div className="grid place-items-center py-6"><Loader2 className="h-4 w-4 animate-spin" style={{ color: "#0B2545" }} /></div>
      ) : messages.length === 0 ? (
        <div className="text-xs text-center py-6" style={{ color: "#94A3B8" }}>
          No messages yet — start a conversation from Team Chat.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="dashboard-chat-list">
          {messages.map((m) => {
            const isMine = m.sender === me?.id;
            return (
              <li key={m.id} className="rounded-lg px-3 py-2"
                  style={{ background: m.is_urgent ? "rgba(155,28,28,0.06)" : "#F8FAFC" }}>
                <div className="flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider"
                     style={{ color: "#7A5E12" }}>
                  <span>{isMine ? `You → ${m.recipient}` : `${m.sender} → you`}</span>
                  <span style={{ color: "#94A3B8" }}>{fmtTime(m.created_at)}</span>
                </div>
                <p className="text-xs mt-0.5 truncate" style={{ color: "#0B2545" }}>
                  {m.body || (m.attachment_name ? `📎 ${m.attachment_name}` : "")}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
